import React from "react";
import { Box, TextField } from "@mui/material";
import { MRT_Row, MRT_ColumnDef } from "material-react-table";
import type { Job } from "../../../../../api/types";
import { getValidationErrorKey } from "../../../../../utilities/jobValidation";
import { ColumnContext } from "../standard-column-types";
import { columnDisplayNames } from "../../../../admin/ImportSettings";
import { useSalespersonPhoneColumnViewModel } from "./SalespersonPhone.column.vm";
import { SalespersonPhoneColumnViewModel } from "./SalespersonPhone.column.types";
import { useValidationErrors } from "../../ValidationErrorsContext";
import { useIsRowInEditMode, useEditModeContext } from "../../EditModeContext";

interface SalespersonPhoneColumnCellProps {
	row: MRT_Row<Job>;
	vm: SalespersonPhoneColumnViewModel;
}

// Module-level storage for cell editing values that survives component remounts
const cellPhoneEditingValues = new Map<string, string>();

/**
 * Memoized Cell component for SalespersonPhone column
 * Prevents unnecessary re-renders when column definitions change
 * Uses local state during editing and only syncs to global state on blur
 */
const SalespersonPhoneColumnCell = React.memo(
	function SalespersonPhoneColumnCell({ row, vm }: SalespersonPhoneColumnCellProps) {
		const job = row.original;
		const validationErrors = useValidationErrors();
		const isInEditMode = useIsRowInEditMode(job.id);
		const { exitRowEdit } = useEditModeContext();
		const [, forceRender] = React.useState(0);
		const storedValue = vm.getPhoneValue(job);
		const isEditable = vm.getIsEditable(job);

		const errorKey = getValidationErrorKey("salespersonMobilePhone", job.id);
		const hasError = isEditable && !!validationErrors?.[errorKey];

		// Unique key for this cell's editing state
		const editKey = `${job.id}-salespersonPhone`;
		const hasEditingValue = cellPhoneEditingValues.has(editKey);
		const currentValue = hasEditingValue ? cellPhoneEditingValues.get(editKey)! : storedValue;

		// View Mode: render phone number for performance
		if (!isInEditMode) {
			return (
				<Box
					title={hasError ? validationErrors[errorKey] : storedValue}
					sx={{
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						color: hasError ? "error.main" : !isEditable ? "#999" : "inherit",
						borderBottom: hasError ? "2px solid" : "none",
						borderColor: "error.main",
						width: "100%",
						px: 0.5,
					}}
				>
					{storedValue || <span style={{ color: "#999" }}>-</span>}
				</Box>
			);
		}

		return (
			<TextField
				size="small"
				style={{ width: "100%", padding: 0 }}
				value={currentValue}
				disabled={!isEditable}
				variant="outlined"
				error={hasError}
				helperText={hasError ? validationErrors[errorKey] : undefined}
				sx={
					hasError
						? {
								"& .MuiOutlinedInput-root": {
									"& fieldset": {
										borderColor: "error.main",
										borderWidth: "2px",
									},
								},
								"& .MuiFormHelperText-root": {
									color: "error.main",
									fontSize: "0.75rem",
								},
							}
						: {}
				}
				slotProps={{
					input: {
						readOnly: !isEditable,
					},
				}}
				onChange={
					isEditable
						? (event) => {
								cellPhoneEditingValues.set(editKey, event.target.value);
								forceRender((n) => n + 1);
							}
						: undefined
				}
				onBlur={() => {
					if (!isEditable) return;
					const editedValue = cellPhoneEditingValues.get(editKey);
					if (editedValue !== undefined && editedValue !== storedValue) {
						vm.handleChange(job.id, editedValue);
					}
					cellPhoneEditingValues.delete(editKey);
				}}
				onKeyDown={(e) => {
					if (e.key === "Escape") {
						cellPhoneEditingValues.delete(editKey);
						exitRowEdit(job.id);
					}
				}}
				placeholder={isEditable ? `Enter phone` : ""}
			/>
		);
	},
	(prevProps, nextProps) => {
		// Custom comparison for better memoization
		const prevJob = prevProps.row.original;
		const nextJob = nextProps.row.original;

		return (
			prevJob.id === nextJob.id &&
			prevJob.salespersonIds?.join(",") === nextJob.salespersonIds?.join(",") &&
			prevJob.newSalesperson?.phone === nextJob.newSalesperson?.phone
		);
	},
);

interface SalespersonPhoneColumnAggregatedCellProps {
	row: MRT_Row<Job>;
	vm: SalespersonPhoneColumnViewModel;
}

// Module-level storage for editing values that survives component remounts
const cellEditingValues = new Map<string, string>();

/**
 * Memoized AggregatedCell component for SalespersonPhone column
 * Renders editable TextField when group row is in edit mode
 * Uses local state during editing and only syncs to global state on blur
 */
const SalespersonPhoneColumnAggregatedCell = React.memo(
	function SalespersonPhoneColumnAggregatedCell({ row, vm }: SalespersonPhoneColumnAggregatedCellProps) {
		// For group rows, check edit mode using row.id (e.g., "grower:Alexander, Curt")
		const isInEditMode = useIsRowInEditMode(row.id);
		const { exitRowEdit } = useEditModeContext();
		const [, forceRender] = React.useState(0);

		// Get value from first job in group
		const job = row.original;
		const storedValue = vm.getPhoneValue(job);
		const isEditable = vm.getIsEditable(job);

		// Unique key for this cell's editing state
		const editKey = `${row.id}-salespersonPhone`;
		const hasEditingValue = cellEditingValues.has(editKey);
		const currentValue = hasEditingValue ? cellEditingValues.get(editKey)! : storedValue;

		// View Mode: render phone number
		if (!isInEditMode) {
			return (
				<Box
					title={storedValue || undefined}
					sx={{
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						color: !isEditable ? "#999" : "inherit",
						width: "100%",
						px: 0.5,
					}}
				>
					{storedValue || <span style={{ color: "#999" }}>-</span>}
				</Box>
			);
		}

		// Edit Mode: render TextField that updates all rows in group on blur
		return (
			<TextField
				size="small"
				style={{ width: "100%", padding: 0 }}
				value={currentValue}
				disabled={!isEditable}
				variant="outlined"
				slotProps={{
					input: {
						readOnly: !isEditable,
					},
				}}
				onChange={
					isEditable
						? (event) => {
								cellEditingValues.set(editKey, event.target.value);
								forceRender((n) => n + 1);
							}
						: undefined
				}
				onBlur={() => {
					if (!isEditable) return;
					const editedValue = cellEditingValues.get(editKey);
					if (editedValue !== undefined && editedValue !== storedValue) {
						vm.handleGroupChange(row, editedValue);
					}
					cellEditingValues.delete(editKey);
				}}
				onKeyDown={(e) => {
					if (e.key === "Escape") {
						cellEditingValues.delete(editKey);
						exitRowEdit(row.id);
					}
				}}
				placeholder={isEditable ? `Enter phone` : ""}
			/>
		);
	},
	(prevProps, nextProps) => {
		const prevJob = prevProps.row.original;
		const nextJob = nextProps.row.original;

		return (
			prevJob.id === nextJob.id &&
			prevJob.salespersonIds?.join(",") === nextJob.salespersonIds?.join(",") &&
			prevJob.newSalesperson?.phone === nextJob.newSalesperson?.phone
		);
	},
);

/**
 * Build the SalespersonPhone column
 * Uses memoized Cell components to prevent unnecessary re-renders when column definitions change
 */
export function column_SalespersonPhone(context: ColumnContext): MRT_ColumnDef<Job, unknown> {
	const vm = useSalespersonPhoneColumnViewModel(context);

	return {
		id: "salespersonMobilePhone",
		header: columnDisplayNames.salespersonMobilePhone,
		accessorFn: () => "", // Dummy accessor since this is a computed column
		enableEditing: false, // We handle editing in the Cell component
		size: 150,
		Cell: ({ row }: { row: MRT_Row<Job> }) => <SalespersonPhoneColumnCell row={row} vm={vm} />,
		AggregatedCell: ({ row }: { row: MRT_Row<Job> }) => <SalespersonPhoneColumnAggregatedCell row={row} vm={vm} />,
	};
}
